import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { isAuthenticated, getUserRole } from '../../../utils/authUtils';
import { getProfileBookings, cancelBooking } from '../../../api/profileApi';
import { logout } from '../../../api/authApi';
import { ADMIN_ROLE } from '../../../constants/constants';
import BookingDetailsModal from './BookingDetailsModal';
import { formatISOStringToReadable } from '../../../utils/dateUtils';
import { translateBookingStatus } from '../../../utils/stranslateUltils';
import './css/Profile.css';
 
const Profile = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(5);
  const navigate = useNavigate();
  const { t } = useTranslation();

    const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load bookings with pagination
      const bookingsRes = await getProfileBookings({ page: currentPage, size: pageSize });

      if (bookingsRes !== null) {
        setBookings(bookingsRes.content || []);
        setTotalPages(bookingsRes.totalPages || 0);
        setTotalElements(bookingsRes.totalElements || 0);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      toast.error(t('profile.load_error'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, t]); 

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/signin');
      return;
    }

    if (getUserRole() === ADMIN_ROLE) {
      navigate('/admin/dashboard');
      return;
    }

    loadProfileData();
  }, [navigate, loadProfileData]);

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t('profile.cancel_confirm'))) {
      return;
    }

    try {
      const res = await cancelBooking({id : bookingId});
      if (res !== null) {
        toast.success(t('profile.cancel_success'));
        // Refresh current page bookings
        loadProfileData();
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error(t('profile.cancel_error'));
    }
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show current page with surrounding pages
      const startPage = Math.max(0, currentPage - 2);
      const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      PENDING: 'status-pending',
      CONFIRMED: 'status-confirmed',
      COMPLETED: 'status-completed',
      CANCELLED: 'status-cancelled'
    };

    return (
      <span className={`profile-status-badge ${statusClasses[status] || ''}`}>
        {translateBookingStatus(t,status)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            {t('profile.home_btn')}
          </button>
          <h1 className="profile-title">{t('profile.heading')}</h1>
          <button 
            className="logout-button"
            onClick={handleLogout}
          >
            {t('profile.logout_btn')}
          </button>
        </div>

        <div className="bookings-section">
          <div className="bookings-header">
            <h2>{t('profile.title')}</h2>
            {totalElements > 0 && (
              <div className="bookings-count">
                {t('profile.bookings_count', {
                  start: currentPage * pageSize + 1,
                  end: Math.min((currentPage + 1) * pageSize, totalElements),
                  total: totalElements
                })}
              </div>
            )}
          </div>

          {bookings.length === 0 ? (
            <div className="no-bookings">
              <p>{t('profile.no_bookings')}</p>
              <button 
                className="book-now-button"
                onClick={() => navigate('/#booking')}
              >
                {t('profile.make_reservation')}
              </button>
            </div>
          ) : (
            <>
              <div className="bookings-list">
                {bookings.map((booking) => (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <div className="booking-date">
                        {formatISOStringToReadable(booking.bookingAt)}
                      </div>
                      {getStatusBadge(booking.status)}
                    </div>
                    
                    <div className="booking-details">
                      <div className="detail-item">
                        <span className="label">{t('profile.guests')}</span>
                        <span className="value">{booking.numGuests}</span>
                      </div>
                      
                      {booking.tables && booking.tables.length > 0 && (
                        <div className="detail-item">
                          <span className="label">{t('profile.tables')}</span>
                          <span className="value">{booking.tables.map(table => table.tableName).join(', ')}</span>
                        </div>
                      )}
                      
                      {booking.note && (
                        <div className="detail-item">
                          <span className="label">{t('profile.note')}</span>
                          <span>{booking.note}</span>
                        </div>
                      )}
                    </div>

                    <div className="booking-actions">
                      <button 
                        className="view-button"
                        onClick={() => handleViewBooking(booking)}
                      >
                        {t('profile.view_details')}
                      </button>
                      
                      {booking.status === 'PENDING' && (
                        <button 
                          className="cancel-button"
                          onClick={() => handleCancelBooking(booking.bookingId)}
                        >
                          {t('profile.cancel_booking')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="profile-pagination-container">
                  <div className="profile-pagination">
                    {/* Previous button */}
                    <button
                      className={`profile-pagination-button ${currentPage === 0 ? 'disabled' : ''}`}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      {t('profile.previous')}
                    </button>

                    {/* Page numbers */}
                    {generatePageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`profile-pagination-button ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum + 1}
                      </button>
                    ))}

                    {/* Next button */}
                    <button
                      className={`profile-pagination-button ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages - 1}
                    >
                      {t('profile.next')}
                    </button>
                  </div>

                  <div className="profile-pagination-info">
                    {t('profile.page_info', { current: currentPage + 1, total: totalPages })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default Profile;